import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import '../styles/Sidebar.css';
import { useAuth } from '../context/AuthContext';

// `tKey` points at a nav.* translation key; labels are resolved at render time
// so the sidebar re-localises instantly when the language changes.
const baseMenu = [
  { id: 'Dashboard', tKey: 'nav.dashboard', icon: '⊞' },
  { id: 'DailyEntry', tKey: 'nav.dailyEntry', icon: '📆' },
  {
    id: 'Master', tKey: 'nav.master', icon: '📋', hasChildren: true,
    children: [
      { id: 'Product', tKey: 'nav.product' },
      { id: 'Tank', tKey: 'nav.tank' },
      { id: 'PumpList', tKey: 'nav.pump' },
      { id: 'NozzleConfig', tKey: 'nav.nozzle' },
      { id: 'Supplier', tKey: 'nav.supplier' },
      { id: 'Customer', tKey: 'nav.customer' },
      { id: 'Employees', tKey: 'nav.employees', icon: '👷' },
    ]
  },
  // {
  //   id: 'Entry', name: 'Entry', icon: '✏️', hasChildren: true,
  //   children: [
  //     { id: 'OpeningStock', name: 'Opening Stock' },
  //     { id: 'ClosingStock', name: 'Closing Stock' },
  //   ]
  // },
  {
    id: 'Sale', tKey: 'nav.sales', icon: '💰', hasChildren: true,
    children: [
      { id: 'SaleList', tKey: 'nav.saleList' },
      { id: 'MeterReading', tKey: 'nav.meterReading' },
      { id: 'LubeSales', tKey: 'nav.lubeSales' },
      { id: 'SaleBillLubeGST', tKey: 'nav.saleBillLubeGst' },
      { id: 'CreditSale', tKey: 'nav.creditSale' },
      { id: 'DailyReceipt', tKey: 'nav.dailyReceipt' },
      { id: 'BillReceipt', tKey: 'nav.billReceipt' },
      { id: 'SaleReturn', tKey: 'nav.saleReturn' },
    ]
  },
  { id: 'ItemLoss', tKey: 'nav.itemLoss', icon: '📉' },
  {
    id: 'Purchase', tKey: 'nav.purchase', icon: '🛒', hasChildren: true,
    children: [
      { id: 'PurchaseOrder', tKey: 'nav.purchase' },
      { id: 'Expenses', tKey: 'nav.expenses' },
      { id: 'PurchaseReturn', tKey: 'nav.purchaseReturn' },
    ]
  },
  {
    id: 'Dip', tKey: 'nav.dip', icon: '📏', hasChildren: true,
    children: [
      { id: 'DipReading', tKey: 'nav.dipReading' },
      { id: 'DipChart', tKey: 'nav.dipChart' },
      { id: 'FuelBalance', tKey: 'nav.fuelBalancing' },
      // Temporarily hidden — re-enable when Tank Transfer / Stock Adjustment are ready.
      // { id: 'TankTransfer', tKey: 'nav.tankTransfer' },
      // { id: 'StockAdjustment', tKey: 'nav.stockAdjustment' },
    ]
  },
  {
    id: 'Accounting', tKey: 'nav.accounting', icon: '📒', hasChildren: true,
    children: [
      { id: 'Accounting', tKey: 'nav.reportsLedger' },
      { id: 'Ledgers', tKey: 'nav.chartOfAccounts', icon: '📒' },
      { id: 'LedgerView', tKey: 'nav.ledgerView', icon: '🔍' },
      { id: 'Payments', tKey: 'nav.payments' },
      { id: 'Contra', tKey: 'nav.contraEntry', icon: '↔️' },
      { id: 'Journal', tKey: 'nav.journalVoucher', icon: '📝' },
      { id: 'FinancialStatements', tKey: 'nav.financialStatements', icon: '📊' },
      { id: 'StockLedger', tKey: 'nav.stockLedger', icon: '📊' },
      { id: 'Banking', tKey: 'nav.bankingFinancials', icon: '🏦' },
      { id: 'Reconciliation', tKey: 'nav.dayReconciliation', icon: '⚖️' },
      { id: 'Overtime', tKey: 'nav.overtime', icon: '⏱️' },
    ]
  },
  { id: 'Support', tKey: 'nav.support', icon: '🎫' },
  { id: 'Sitemap', tKey: 'nav.sitemap', icon: '🗺️' },
  { id: 'RegisterStation', tKey: 'nav.registerStation', icon: '➕' },
];

// Admin-only items — appended conditionally
const adminMenu = [
  { id: 'AuditLog', tKey: 'nav.auditLog', icon: '🛡️' },
  { id: 'UserManagement', tKey: 'nav.usersRoles', icon: '👥' },
];

// Noris-team-only items (platform admins) — appended conditionally
const norisMenu = [
  { id: 'Approvals', tKey: 'nav.stationApprovals', icon: '🛡️' },
];

function getParentId(childId, items) {
  for (const item of items) {
    if (item.hasChildren && item.children.some(c => c.id === childId)) {
      return item.id;
    }
  }
  return null;
}

export default function Sidebar({ activePage, setActivePage, mobileOpen, onClose }) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  const { user, logout, isTrialExpired, isNorisTeam, canAccess } = useAuth();

  // Mapping of Sidebar ID -> Module Name (for permissions)
  const menuModuleMap = {
    'Dashboard': 'dashboard',
    'Product': 'products',
    'Tank': 'tanks',
    'PumpList': 'pumps',
    'NozzleConfig': 'pumps',
    'Supplier': 'suppliers',
    'Customer': 'customers',
    'Employees': 'employees',
    'SaleList': 'sales',
    'MeterReading': 'meter_readings',
    'LubeSales': 'lube_sales',
    'SaleBillLubeGST': 'lube_sales',
    'CreditSale': 'credit_sales',
    'DailyReceipt': 'daily_receipts',
    'BillReceipt': 'bill_receipts',
    'SaleReturn': 'sales',
    'ItemLoss': 'item_loss',
    'PurchaseOrder': 'purchases',
    'Expenses': 'expenses',
    'PurchaseReturn': 'purchases',
    'DipReading': 'dip_readings',
    'DipChart': 'dip_readings',
    'FuelBalance': 'fuel_balance',
    'TankTransfer': 'stock_ledger',
    'StockAdjustment': 'stock_ledger',
    'Accounting': 'accounting',
    'Ledgers': 'accounting',
    'LedgerView': 'accounting',
    'Payments': 'payments',
    'Contra': 'accounting',
    'Journal': 'accounting',
    'FinancialStatements': 'financials',
    'StockLedger': 'stock_ledger',
    'Banking': 'banks',
    'Reconciliation': 'reconciliation',
    'Overtime': 'overtime',
    // Support has no backend RBAC gate (tickets.php only calls requireAuth) and
    // the manual keeps it always reachable. Leaving it unmapped makes filterMenu
    // fall through to "no module → always show" (same as Dashboard).
    'RegisterStation': 'stations',
    'AuditLog': 'audit_logs',
    'UserManagement': 'roles',
    'Approvals': 'stations'
  };

  const filterMenu = (items) => {
    return items
      .map(item => {
        if (item.hasChildren) {
          const visibleChildren = filterMenu(item.children);
          if (visibleChildren.length === 0) return null;
          return { ...item, children: visibleChildren };
        }
        const module = menuModuleMap[item.id];
        if (!module) return item; // Items without module mapping (e.g. Dashboard if not in DB)
        return canAccess(module) ? item : null;
      })
      .filter(Boolean);
  };

  const menuItems = [
    ...filterMenu(baseMenu),
    ...(isNorisTeam ? norisMenu : []),
    ...filterMenu(adminMenu),
  ];

  useEffect(() => {
    const parentId = getParentId(activePage, menuItems);
    if (parentId) {
      setOpenMenus(prev => ({
        ...prev,
        [parentId]: true
      })); // Accordion: only one menu open
    }
    // eslint-disable-next-line
  }, [activePage]);

  const toggleMenu = (id) => {
    setOpenMenus(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleClick = (item) => {
    if (isTrialExpired && item.id !== 'Dashboard' && item.id !== 'Support') return;
    if (item.hasChildren) {
      if (!collapsed) {
        toggleMenu(item.id);
      }
      return;
    } else {
      setActivePage(item.id);
    }
  };

  const isParentActive = (item) => {
    if (!item.hasChildren) return false;
    return item.children.some(c => c.id === activePage) || activePage === item.id;
  };

  return (
    <div className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
      <div className="sidebar-logo">
        <div className="logo-icon">
          <img src="https://noris.in/Assets/N%20logo.webp" alt="Noris"
            style={{ width: 28, height: 28, objectFit: 'contain' }}
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
          <span style={{ display: 'none', color: 'white', fontWeight: 900, fontSize: 16 }}>N</span>
        </div>
        {!collapsed && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0, flex: 1, overflow: 'hidden' }}>
            <span className="logo-text">{t('app.brand')}</span>
            <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.5px' }}>{t('app.brandBy')}</span>
          </div>
        )}
        <button className="collapse-btn" onClick={() => setCollapsed(!collapsed)}>
          {collapsed ? '▶' : '◀'}
        </button>
      </div>

      <nav className="sidebar-nav">
        {menuItems.map((item) => (
          <div key={item.id}>
            <div
              className={`sidebar-item ${activePage === item.id || isParentActive(item) ? 'active' : ''} ${isTrialExpired && item.id !== 'Dashboard' && item.id !== 'Support' ? 'disabled' : ''
                }`}
              onClick={() => handleClick(item)}
            >
              <span className="item-icon">{item.icon}</span>
              {!collapsed && (
                <>
                  <span className="item-name">{t(item.tKey)}</span>
                  {item.hasChildren && (
                    <span className="arrow" style={{
                      transform: openMenus[item.id] ? 'rotate(90deg)' : 'rotate(-90deg)',
                      transition: 'transform 0.2s'
                    }}>
                      ‹
                    </span>
                  )}
                </>
              )}
            </div>

            {!collapsed && item.hasChildren && openMenus[item.id] && (
              <div className="sidebar-submenu">
                {item.children.map((child) => (
                  <div
                    key={child.id}
                    className={`sidebar-item ${activePage === child.id ? 'active' : ''} ${isTrialExpired && child.id !== 'Dashboard' && child.id !== 'Support' ? 'disabled' : ''
                      }`}
                    onClick={() => {
                      if (isTrialExpired && child.id !== 'Dashboard' && child.id !== 'Support') return;
                      setActivePage(child.id);
                    }}
                  >
                    <span className="item-icon">›</span>
                    <span className="item-name">{t(child.tKey)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-item logout-item" onClick={logout} title={t('nav.logout')}>
          <span className="item-icon">⏻</span>
          {!collapsed && <span className="item-name">{t('nav.logout')}</span>}
        </div>
      </div>
    </div>
  );
}
